import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const fetchCutLists = createAsyncThunk(
  'cutlists/fetchCutLists',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/cutlists', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij ophalen snijlijsten');
    }
  }
);

export const fetchCutList = createAsyncThunk(
  'cutlists/fetchCutList',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/cutlists/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij ophalen snijlijst');
    }
  }
);

export const createCutList = createAsyncThunk(
  'cutlists/createCutList',
  async (cutListData, { rejectWithValue }) => {
    try {
      const response = await api.post('/cutlists', cutListData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij aanmaken snijlijst');
    }
  }
);

export const generateCutLists = createAsyncThunk(
  'cutlists/generateCutLists',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.post('/cutlists/generate', params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij genereren snijlijsten');
    }
  }
);

export const previewCutLists = createAsyncThunk(
  'cutlists/previewCutLists',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.post('/cutlists/preview', params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij preview snijlijsten');
    }
  }
);

export const updateCutListStatus = createAsyncThunk(
  'cutlists/updateCutListStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/cutlists/${id}/status`, { status });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij bijwerken status');
    }
  }
);

export const updateCutListMachine = createAsyncThunk(
  'cutlists/updateCutListMachine',
  async ({ id, machineId }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/cutlists/${id}/machine`, { machineId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij wijzigen machine');
    }
  }
);

export const moveItemsToMachine = createAsyncThunk(
  'cutlists/moveItemsToMachine',
  async ({ id, itemIds, targetMachineId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/cutlists/${id}/move-items`, { itemIds, targetMachineId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij verplaatsen items');
    }
  }
);

export const updateItemStatus = createAsyncThunk(
  'cutlists/updateItemStatus',
  async ({ cutListId, itemId, status }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/cutlists/${cutListId}/items/${itemId}/status`, { status });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij bijwerken item status');
    }
  }
);

export const reorderItems = createAsyncThunk(
  'cutlists/reorderItems',
  async ({ id, itemIds }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/cutlists/${id}/reorder`, { itemIds });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij herordenen items');
    }
  }
);

export const deleteCutList = createAsyncThunk(
  'cutlists/deleteCutList',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/cutlists/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij verwijderen snijlijst');
    }
  }
);

export const deleteAllCutLists = createAsyncThunk(
  'cutlists/deleteAllCutLists',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.machineIds) queryParams.append('machineIds', params.machineIds.join(','));
      if (params.status) queryParams.append('status', params.status);
      const response = await api.delete(`/cutlists/all?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij verwijderen snijlijsten');
    }
  }
);

export const fetchOrdersForMachine = createAsyncThunk(
  'cutlists/fetchOrdersForMachine',
  async (machineId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/cutlists/orders-for-machine/${machineId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij ophalen orders');
    }
  }
);

const initialState = {
  cutLists: [],
  currentCutList: null,
  ordersForMachine: [],
  preview: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  },
  loading: false,
  generating: false,
  previewing: false,
  error: null
};

const cutlistsSlice = createSlice({
  name: 'cutlists',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCutList: (state) => {
      state.currentCutList = null;
    },
    clearPreview: (state) => {
      state.preview = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCutLists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCutLists.fulfilled, (state, action) => {
        state.loading = false;
        state.cutLists = action.payload.cutLists;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCutLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCutList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCutList.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCutList = action.payload;
      })
      .addCase(fetchCutList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCutList.fulfilled, (state, action) => {
        state.cutLists.unshift(action.payload);
      })
      .addCase(generateCutLists.pending, (state) => {
        state.generating = true;
        state.error = null;
      })
      .addCase(generateCutLists.fulfilled, (state, action) => {
        state.generating = false;
        state.preview = null; // Clear preview after generating
        if (action.payload.cutLists) {
          state.cutLists = [...action.payload.cutLists, ...state.cutLists];
        }
      })
      .addCase(generateCutLists.rejected, (state, action) => {
        state.generating = false;
        state.error = action.payload;
      })
      .addCase(previewCutLists.pending, (state) => {
        state.previewing = true;
        state.error = null;
      })
      .addCase(previewCutLists.fulfilled, (state, action) => {
        state.previewing = false;
        state.preview = action.payload;
      })
      .addCase(previewCutLists.rejected, (state, action) => {
        state.previewing = false;
        state.error = action.payload;
      })
      .addCase(updateCutListStatus.fulfilled, (state, action) => {
        state.error = null;
        const index = state.cutLists.findIndex(c => c._id === action.payload._id);
        if (index !== -1) {
          state.cutLists[index] = action.payload;
        }
        if (state.currentCutList?._id === action.payload._id) {
          state.currentCutList = action.payload;
        }
      })
      .addCase(updateCutListStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateCutListMachine.fulfilled, (state, action) => {
        const index = state.cutLists.findIndex(c => c._id === action.payload._id);
        if (index !== -1) {
          state.cutLists[index] = action.payload;
        }
        if (state.currentCutList?._id === action.payload._id) {
          state.currentCutList = action.payload;
        }
      })
      .addCase(moveItemsToMachine.fulfilled, (state, action) => {
        // Update or remove source cutlist
        if (action.payload.sourceDeleted) {
          state.cutLists = state.cutLists.filter(c => c._id !== state.currentCutList?._id);
          state.currentCutList = null;
        } else if (action.payload.sourceCutList) {
          const index = state.cutLists.findIndex(c => c._id === action.payload.sourceCutList._id);
          if (index !== -1) {
            state.cutLists[index] = action.payload.sourceCutList;
          }
          if (state.currentCutList?._id === action.payload.sourceCutList._id) {
            state.currentCutList = action.payload.sourceCutList;
          }
        }
        // Add or update target cutlist in the list
        if (action.payload.targetCutList) {
          const targetIndex = state.cutLists.findIndex(c => c._id === action.payload.targetCutList._id);
          if (targetIndex !== -1) {
            state.cutLists[targetIndex] = action.payload.targetCutList;
          } else {
            state.cutLists.unshift(action.payload.targetCutList);
          }
        }
      })
      .addCase(updateItemStatus.fulfilled, (state, action) => {
        if (state.currentCutList?._id === action.payload._id) {
          state.currentCutList = action.payload;
        }
      })
      .addCase(reorderItems.fulfilled, (state, action) => {
        if (state.currentCutList?._id === action.payload._id) {
          state.currentCutList = action.payload;
        }
      })
      .addCase(deleteCutList.fulfilled, (state, action) => {
        state.cutLists = state.cutLists.filter(c => c._id !== action.payload);
      })
      .addCase(deleteAllCutLists.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAllCutLists.fulfilled, (state) => {
        state.loading = false;
        // Filter out draft/ready/cancelled cutlists (the ones that were deleted)
        state.cutLists = state.cutLists.filter(c =>
          c.status === 'in_progress' || c.status === 'completed'
        );
      })
      .addCase(deleteAllCutLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOrdersForMachine.fulfilled, (state, action) => {
        state.ordersForMachine = action.payload.orders || [];
      });
  }
});

export const { clearError, clearCurrentCutList, clearPreview } = cutlistsSlice.actions;
export default cutlistsSlice.reducer;
